import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import CardPeacock from './PeacockCheckoutCard';
import CardCheckout from './CheckoutCard';
import CustomInput from './CustomInput';
import CustomDropDown from './CustomDropDown';
import industries from '../utils/industries';
import getDemoLink, { shouldShowPromoCodeMessage } from '../utils/helpers';
import fetchStatesDropdownOptions from '../services/states';
import './checkoutGettingStarted.scss';

function CheckoutGettingStarted({
  selectedCard,
  updateForm,
  values,
  devicePrice,
  loading,
  devicePriceFee,
  formErrors,
  shippingPriceFee,
  renderButtons,
  showFireCode = false,
  peacockMonthlyPrices,
  peacockYearlyPrices,
  setForm,
}) {
  const Card = showFireCode ? CardPeacock : CardCheckout;
  const defaultPeacockMonthlyValue = peacockMonthlyPrices && peacockMonthlyPrices.length > 0 ? peacockMonthlyPrices[0].value : 0;
  const defaultPeacockYearlyValue = peacockYearlyPrices && peacockYearlyPrices.length > 0 ? peacockYearlyPrices[0].value : 0;
  const [USStatesDropdownOptions, setUSStatesDropdownOptions] = useState([]);
  const [promoCodeMessageVisible, setPromoCodeMessageVisible] = useState(true);
  const [billingPlan, setBillingPlan] = useState(values.billingPlan ? values.billingPlan : 'monthly');
  const defaultPeacockFee = billingPlan === 'monthly' ? defaultPeacockMonthlyValue : defaultPeacockYearlyValue;

  useEffect(async () => {
    const options = await fetchStatesDropdownOptions();
    setUSStatesDropdownOptions(options);
  }, []);

  useEffect(() => {
    if (showFireCode) handleFireCodeChange({ target: { value: values.fireCode } });
  }, [billingPlan, showFireCode]);

  const closePromoCodeModal = () => setPromoCodeMessageVisible(false);

  const handleChange = e => {
    updateForm(e);
  };

  const handleDropdownChange = e => {
    updateForm(e);
  };

  const handleTabsChange = plan => {
    setBillingPlan(plan);
  };

  const getFireCodePrice = fco => {
    const prices = billingPlan === 'monthly' ? peacockMonthlyPrices : peacockYearlyPrices;
    const defaultValue = billingPlan === 'monthly' ? defaultPeacockMonthlyValue : defaultPeacockYearlyValue;

    if (!fco || fco < 1) return defaultValue;
    return prices.find(p => p.minFCO <= fco && (!p.maxFCO || p.maxFCO >= fco));
  };
  const handleShippingChange = option =>
    setForm({
      ...values,
      shipping: option,
    });

  const handleFireCodeChange = e => {
    const value = parseInt(e.target.value, 10);

    setForm({
      ...values,
      fireCodePrice: getFireCodePrice(value).value,
      billingPlan,
      fireCode: value,
    });
  };

  const showPromoCode = promoCodeMessageVisible && showFireCode && shouldShowPromoCodeMessage();

  return (
    <>
      <div id="containerCheckout">
        <Dialog
          header="Concerned your device won't arrive on time?"
          visible={showPromoCode}
          draggable={false}
          resizable={false}
          focusOnShow={false}
          onMaskClick={closePromoCodeModal}
          onHide={closePromoCodeModal}
          footer={<Button label="Got it!" className="btn-magenta" onClick={closePromoCodeModal} />}>
          <p className="m-0">
            Upon sign-up you&apos;ll be provided with a back-up way to get the games using a Peacock insta-watch code so you
            don&apos;t miss a second of the action!
            <br />
            <b>
              Any orders after 5PM ET on Saturday are not guaranteed to be processed in time for the Chiefs vs. Dolphins Wild Card
              on January 13.
            </b>
          </p>
        </Dialog>
        <div className="containerCheckout">
          <div className="grid main_container">
            <div className="data_container col-7">
              <div className="grid">
                <div className="col-12 flex align-items-center header">
                  <div className="header_number">1</div>
                  <div className="header_text_box">
                    <p className="step_text_mobile">Step 1 of 4</p>
                    <h2 className="header_title">Location Information</h2>
                  </div>
                </div>
                <div className="form_conatiner">
                  <div className="grid">
                    <div className="col-12 paraf">
                      <p className="description">
                        This sign-up process is for one device. Upon completion you can add additional device(s).
                      </p>
                      <p className="description">
                        Signing up for more than 1 device?{' '}
                        <a href={getDemoLink(showFireCode)}> Set up a time to meet with an UPshow implementation specialist.</a>
                      </p>
                    </div>
                    <div className="inputs_box col-12 flex align-items-start padding_0">
                      <div className="col-6">
                        <div className="field title-input">
                          <span className="block label required_icon">Business name</span>
                          <CustomInput
                            error={formErrors.businessName}
                            errorMessage={formErrors.businessName}
                            name="businessName"
                            value={values.businessName || ''}
                            onChange={handleChange}
                            placeholder="Business name"
                            type="text"
                            tooltip="Enter your Business Name"
                            tooltipOptions={{ position: 'bottom' }}
                            disabled={loading}
                          />
                        </div>
                      </div>
                      {!showFireCode ? (
                        <div className="col-6">
                          <div className="field title-input">
                            <span className="block label required_icon">Industry</span>
                            <CustomDropDown
                              error={formErrors.industry}
                              errorMessage={formErrors.industry}
                              value={values.industry}
                              options={industries}
                              onChange={handleDropdownChange}
                              placeholder="Industry"
                              name="industry"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="col-6">
                          <div className="field title-input">
                            <span className="block label required_icon">Fire Code Occupancy (FCO)</span>
                            <CustomInput
                              error={formErrors.fireCode}
                              errorMessage={formErrors.fireCode}
                              name="fireCode"
                              value={values.fireCode || ''}
                              onChange={handleFireCodeChange}
                              placeholder="Fire Code Occupancy"
                              type="number"
                              disabled={loading}
                              helper="Enter a number between 1 and 5000."
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {showFireCode && (
                      <div className="col-12 paraf mt-0">
                        <p>
                          <b>Note: </b>You may be required to provide additional verification of your FCO at a later date
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="col-12 sub-header padding_0">
                    <h3>Business Address</h3>
                  </div>
                  <div className="grid">
                    <div className="col-12 flex inputs_box padding_0">
                      <div className="col-6">
                        <div className="field title-input">
                          <span className="block label required_icon" htmlFor="businessAddress">
                            Address Line 1
                          </span>
                          <CustomInput
                            error={formErrors.businessAddress}
                            errorMessage={formErrors.businessAddress}
                            value={values.businessAddress || ''}
                            onChange={handleChange}
                            name="businessAddress"
                            type="text"
                            placeholder="Street Address..."
                          />
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="field title-input">
                          <span className="block label" htmlFor="businessAddress2">
                            Address Line 2
                          </span>
                          <CustomInput
                            error={formErrors.businessAddress2}
                            errorMessage={formErrors.businessAddress2}
                            value={values.businessAddress2 || ''}
                            onChange={handleChange}
                            name="businessAddress2"
                            type="text"
                            placeholder="Street Address..."
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex inputs_box col-12 padding_0">
                      <div className="flex left_box col-6 padding_0">
                        <div className="col-6">
                          <div className="p-field title-input">
                            <span className="block label required_icon" htmlFor="city">
                              City
                            </span>
                            <CustomInput
                              error={formErrors.city}
                              errorMessage={formErrors.city}
                              value={values.city}
                              onChange={handleChange}
                              name="city"
                              placeholder="City..."
                            />
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="field title-input">
                            <span className="block label required_icon" htmlFor="zipCode">
                              Zip code
                            </span>
                            <CustomInput
                              error={formErrors.zipCode}
                              errorMessage={formErrors.zipCode}
                              value={values.zipCode || ''}
                              onChange={handleChange}
                              name="zipCode"
                              type="text"
                              placeholder="Zip Code..."
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="field title-input">
                          <span className="block label required_icon" htmlFor="shippingState">
                            State / Province
                          </span>
                          <CustomDropDown
                            error={formErrors.state}
                            errorMessage={formErrors.state}
                            value={values.state}
                            options={USStatesDropdownOptions}
                            onChange={handleChange}
                            placeholder={USStatesDropdownOptions.length === 0 ? 'Loading' : 'State / Province...'}
                            name="state"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="country_input col-12">
                      <div className="field title-input">
                        <span className="block label" htmlFor="shippingCountry">
                          Country
                        </span>
                        <CustomInput
                          error={formErrors.country}
                          errorMessage={formErrors.country}
                          value={values.country}
                          onChange={handleChange}
                          name="country"
                          placeholder="Country..."
                          disabled
                        />
                      </div>
                      <div className="col-12 paraf padding_0">
                        <p>
                          <b>Note: </b>If you live outside of the United States, please contact our&nbsp;
                          <a href={getDemoLink(showFireCode)} target="_blank" rel="noopener noreferrer">
                            Customer Success
                          </a>
                          &nbsp;team so we can get you set up
                        </p>
                      </div>
                    </div>
                  </div>
                  {renderButtons()}
                </div>
              </div>
            </div>
            <div className={`card_container col-5 mt-5 ${showFireCode ? 'plans' : ''}`}>
              {showFireCode && (
                <div className="plan_container">
                  <h3>Select Package</h3>
                  <button
                    onClick={() => handleTabsChange('annually')}
                    type="button"
                    className={billingPlan === 'annually' ? 'active' : ''}>
                    Annually
                  </button>
                  <button
                    onClick={() => handleTabsChange('monthly')}
                    type="button"
                    className={billingPlan === 'monthly' ? 'active' : ''}>
                    Monthly
                  </button>
                </div>
              )}
              <Card
                id={selectedCard?.id}
                title={selectedCard?.title}
                price={selectedCard?.priceText}
                icon={selectedCard?.icon}
                checkList={selectedCard?.checklistP}
                devicePrice={devicePrice}
                numberOfDevices={values.numberOfDevices}
                loading={loading}
                devicePriceFee={devicePriceFee}
                shippingPriceFee={shippingPriceFee}
                peacockFee={values.fireCodePrice ?? defaultPeacockFee}
                billingPlan={billingPlan}
                shippingType={values.shipping}
                setShipping={handleShippingChange}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

CheckoutGettingStarted.propTypes = {
  values: PropTypes.shape({
    businessName: PropTypes.string.isRequired,
    industry: PropTypes.string.isRequired,
    fireCodePrice: PropTypes.number,
    fireCode: PropTypes.number,
    numberOfDevices: PropTypes.number.isRequired,
    source: PropTypes.string,
    atn: PropTypes.string,
    businessAddress: PropTypes.string,
    businessAddress2: PropTypes.string,
    city: PropTypes.string,
    zipCode: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    billingPlan: PropTypes.string,
    shipping: PropTypes.string,
  }).isRequired,
  updateForm: PropTypes.func.isRequired,
  devicePrice: PropTypes.number,
  devicePriceFee: PropTypes.number,
  shippingPriceFee: PropTypes.number,
  loading: PropTypes.bool.isRequired,
  selectedCard: PropTypes.objectOf(PropTypes.any),
  formErrors: PropTypes.objectOf(PropTypes.any),
  renderButtons: PropTypes.any,
  showFireCode: PropTypes.bool,
  peacockMonthlyPrices: PropTypes.any,
  peacockYearlyPrices: PropTypes.any,
  setForm: PropTypes.func.isRequired,
};

CheckoutGettingStarted.defaultProps = {
  devicePrice: undefined,
  devicePriceFee: undefined,
  shippingPriceFee: undefined,
  formErrors: undefined,
  selectedCard: undefined,
  showFireCode: false,
  peacockMonthlyPrices: undefined,
  peacockYearlyPrices: undefined,
  renderButtons: () => {},
};

export default CheckoutGettingStarted;
